import { Button } from "@groot/ui/button";
import { Page } from "@/app/shared/components/page";
import { ModelSelect } from "@/app/shared/components/model-select";
import { PageHeader } from "@groot/shell/components/layout";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Account } from "@/types";
import { CheckCircle, Clock, FileText, Upload, XCircle, BoxSelect, Trash2 } from "lucide-react";
import { Badge } from "@groot/ui/badge";
import { Checkbox } from "@groot/ui/checkbox";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "@groot/ui/primitives";

// Local Statement interface that matches the API response structure
interface StatementLocal {
  id: number;
  fileName: string;
  fileS3Path: string;
  fileSize?: number | null;
  accountId?: number | null;
  uploadedAt: string;
  status: "pending" | "processing" | "completed" | "failed";
  extractedData?: {
    transactions?: Array<{
      date: string;
      description: string;
      amount: number;
      type: "credit" | "debit";
      referenceNo?: string;
      balance?: number;
    }>;
    accountNumber?: string;
    statementPeriod?: {
      from: string;
      to: string;
    };
    openingBalance?: number;
    closingBalance?: number;
  } | null;
  errorMessage?: string | null;
  transactionsCreated?: number | null;
}

export function StatementImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [accountId, setAccountId] = useState<string>("");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [statements, setStatements] = useState<StatementLocal[]>([]);
  const [selectedStatementIds, setSelectedStatementIds] = useState<number[]>([]);
  const [bulkExtracting, setBulkExtracting] = useState(false);
  const [bulkExtractDialogOpen, setBulkExtractDialogOpen] = useState(false);
  const [bulkAutoUpload, setBulkAutoUpload] = useState(true);
  const [bulkRenameFile, setBulkRenameFile] = useState(false);
  const [bulkIgnorePagesFromEnd, setBulkIgnorePagesFromEnd] = useState<number | undefined>(
    undefined,
  );
  const [bulkAiModel, setBulkAiModel] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  useEffect(() => {
    loadAccounts();
    loadStatements();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await apiClient.getAccounts();
      setAccounts(data.filter((acc: Account) => !acc.isDeleted && !acc.isArchived));
    } catch {
      toast({
        title: "Error",
        description: "Failed to load accounts",
        variant: "destructive",
      });
    }
  };

  const loadStatements = async () => {
    try {
      setLoading(true);
      const statements = await apiClient.getStatements();
      // Cast to local StatementLocal type which is compatible with the API response
      setStatements((statements || []) as unknown as StatementLocal[]);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load statements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleStatementSelect = (id: number) => {
    setSelectedStatementIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const selectAllStatements = () => {
    setSelectedStatementIds((prev) =>
      prev.length === statements.length ? [] : statements.map((s) => s.id),
    );
  };

  const handleBulkExtract = async () => {
    if (selectedStatementIds.length === 0) return;
    try {
      setBulkExtracting(true);
      const results = await Promise.allSettled(
        selectedStatementIds.map((sid) =>
          apiClient.extractStatement(sid, {
            autoUpload: bulkAutoUpload,
            renameFile: bulkRenameFile,
            ignorePagesFromEnd: bulkIgnorePagesFromEnd,
            aiModel: bulkAiModel,
          }),
        ),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      toast({
        title: "Queued",
        description: `Extraction queued for ${succeeded} statements${failed ? `, ${failed} failed` : ""}`,
      });
      setSelectedStatementIds([]);
      await loadStatements();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to queue extraction",
        variant: "destructive",
      });
    } finally {
      setBulkExtracting(false);
      setBulkExtractDialogOpen(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== "application/pdf") {
        toast({
          title: "Invalid file",
          description: "Please select a PDF file",
          variant: "destructive",
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a PDF file to upload",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      if (accountId) {
        formData.append("accountId", accountId);
      }

      const result = await apiClient.uploadStatement(formData);

      toast({
        title: "Success",
        description: "Statement uploaded successfully. Select areas to extract...",
      });

      setFile(null);
      setAccountId("");

      // Redirect to annotation page
      const uploadedStatement = result as { id?: number } | undefined;
      if (uploadedStatement?.id) {
        navigate(`/statements/${uploadedStatement.id}/annotate`);
      } else {
        loadStatements();
      }
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload statement",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="size-5 text-success" />;
      case "failed":
        return <XCircle className="size-5 text-destructive" />;
      case "processing":
        return <Clock className="size-5 text-info animate-spin" />;
      default:
        return <Clock className="size-5 text-muted-foreground/70" />;
    }
  };

  const getStatementBadge = (st: StatementLocal) => {
    // `Badge` has no info/success tones; `variant={null}` renders the same
    // untoned pill those unknown variant names always produced.
    if (st.status === "processing") {
      return <Badge variant={null}>Processing</Badge>;
    }
    const txs = Array.isArray(st.extractedData?.transactions) ? st.extractedData?.transactions : [];
    if (txs.length > 0) {
      return <Badge variant={null}>Extracted</Badge>;
    }
    return null;
  };

  return (
    <>
      <Page>
        {/* Header */}
        <PageHeader
          title="Import Statements"
          description="Upload bank, credit card, or loan statements to extract transactions"
          className="mb-8"
        />

        <div className="space-y-6">
          {/* Upload Section */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4">
              <h2 className="text-base font-medium text-foreground flex items-center gap-2">
                <Upload className="size-5" />
                Upload Statement
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file">Statement PDF</Label>
                <Input
                  id="file"
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {file && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="account">Account (Optional)</Label>
                <Select value={accountId} onValueChange={setAccountId} disabled={uploading}>
                  <SelectTrigger id="account">
                    <SelectValue placeholder="No account (review later)" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id.toString()}>
                        {account.accountName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleUpload} disabled={!file || uploading} className="w-full">
                {uploading ? (
                  <>
                    <LoadingSpinner className="size-4" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="size-4" />
                    Upload & Extract
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Statements List */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4 flex items-center justify-between">
              <h2 className="text-base font-medium text-foreground flex items-center gap-2">
                <FileText className="size-5" />
                Recent Statements
              </h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={selectAllStatements} className="gap-2">
                  <BoxSelect className="w-4 h-4" />
                  {selectedStatementIds.length === statements.length && statements.length > 0
                    ? "Unselect all"
                    : "Select all"}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setBulkExtractDialogOpen(true)}
                  disabled={selectedStatementIds.length === 0 || bulkExtracting}
                  className="gap-2"
                >
                  Extract whole PDF
                </Button>
                <Button variant="outline" size="sm" onClick={loadStatements}>
                  Refresh
                </Button>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <LoadingSpinner size="lg" className="py-12" />
              ) : statements.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">
                  No statements uploaded yet
                </p>
              ) : (
                <div className="space-y-2">
                  {statements.map((statement) => (
                    <div
                      key={statement.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted cursor-pointer"
                      onClick={() => navigate(`/statements/${statement.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          navigate(`/statements/${statement.id}`);
                        }
                      }}
                      // biome-ignore lint/a11y/useSemanticElements: simplified clickable div
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Checkbox
                          checked={selectedStatementIds.includes(statement.id)}
                          onCheckedChange={() => toggleStatementSelect(statement.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {getStatusIcon(statement.status)}
                        <div className="flex-1">
                          <p className="font-medium">{statement.fileName}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(statement.uploadedAt).toLocaleString()}
                            {statement.extractedData?.transactions && (
                              <> • {statement.extractedData.transactions.length} transactions</>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatementBadge(statement)}
                        {statement.status === "failed" && statement.errorMessage && (
                          <p className="text-sm text-destructive max-w-md truncate">
                            {statement.errorMessage}
                          </p>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (
                              await confirm({
                                title: "Delete Statement",
                                description:
                                  "Are you sure you want to delete this statement? This action cannot be undone and will delete the associated file.",
                                destructive: true,
                              })
                            ) {
                              try {
                                await apiClient.deleteStatement(statement.id);
                                toast({
                                  title: "Success",
                                  description: "Statement deleted successfully",
                                });
                                loadStatements();
                              } catch {
                                toast({
                                  title: "Error",
                                  description: "Failed to delete statement",
                                  variant: "destructive",
                                });
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Page>
      <Dialog open={bulkExtractDialogOpen} onOpenChange={setBulkExtractDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm bulk extraction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Do you want to automatically upload extracted transactions after extraction for the
              selected statements?
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="bulk-auto-upload-checkbox"
                checked={bulkAutoUpload}
                onCheckedChange={(v) => setBulkAutoUpload(Boolean(v))}
              />
              <label htmlFor="bulk-auto-upload-checkbox" className="text-sm">
                Auto-upload extracted transactions
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="bulk-rename-file-checkbox"
                checked={bulkRenameFile}
                onCheckedChange={(v) => setBulkRenameFile(Boolean(v))}
              />
              <label htmlFor="bulk-rename-file-checkbox" className="text-sm">
                Rename file to YYYY-MM.pdf
              </label>
            </div>
            <div>
              <Label htmlFor="bulk-ignore-pages-end" className="text-sm">
                Ignore pages from end
              </Label>
              <Input
                id="bulk-ignore-pages-end"
                type="number"
                min="0"
                placeholder="0"
                value={bulkIgnorePagesFromEnd ?? ""}
                onChange={(e) =>
                  setBulkIgnorePagesFromEnd(e.target.value ? Number(e.target.value) : undefined)
                }
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Skip last X pages (e.g., T&C, ads)
              </p>
            </div>
            <ModelSelect value={bulkAiModel} onChange={setBulkAiModel} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkExtractDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkExtract} disabled={bulkExtracting}>
                {bulkExtracting ? <LoadingSpinner className="size-4" /> : null}
                Extract
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
